import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ComplianceOfficerSOP = () => {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <div className="max-w-4xl space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard/help")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Help Center
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Compliance Officer Guide</h1>
          </div>
          <p className="text-muted-foreground">
            Complete guide for managing NIL deal compliance, reviews, and approvals
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
            <CardDescription>
              As a Compliance Officer, you are responsible for reviewing, approving, and monitoring all NIL deals
              to ensure compliance with NCAA regulations, state laws, and institutional policies.
            </CardDescription>
          </CardHeader>
        </Card>

        <Accordion type="single" collapsible className="space-y-4">
          <AccordionItem value="reviewing-deals">
            <AccordionTrigger className="text-lg font-semibold">
              How to Review and Approve NIL Deals
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Step 1: Access Pending Deals</h4>
                <p className="text-muted-foreground">
                  Navigate to Dashboard → Pending Deals to view all deals awaiting review.
                </p>

                <h4 className="font-semibold">Step 2: Review Deal Details</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Verify athlete eligibility status</li>
                  <li>Check brand name against institutional sponsors and prohibited categories</li>
                  <li>Review deal amount and compensation structure</li>
                  <li>Examine deliverables and time commitment</li>
                  <li>Check for conflicts with existing deals</li>
                </ul>

                <h4 className="font-semibold">Step 3: Review Documents</h4>
                <p className="text-muted-foreground">
                  Click on the deal to view uploaded contracts and supporting documents. Ensure all required
                  documentation is present and properly executed.
                </p>

                <h4 className="font-semibold">Step 4: Check Compliance Flags</h4>
                <p className="text-muted-foreground">
                  The system automatically flags potential issues. Review all compliance warnings including:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Conflicts with institutional sponsors</li>
                  <li>Prohibited categories or brands</li>
                  <li>High-value thresholds</li>
                  <li>Missing documentation</li>
                </ul>

                <h4 className="font-semibold">Step 5: Make Decision</h4>
                <p className="text-muted-foreground">
                  Click "Review Deal" and select:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li><strong>Approve:</strong> Deal meets all compliance requirements</li>
                  <li><strong>Reject:</strong> Deal violates policies (provide detailed reason)</li>
                  <li><strong>Request More Info:</strong> Need additional documentation or clarification</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="risk-levels">
            <AccordionTrigger className="text-lg font-semibold">
              Understanding Risk Levels and Compliance Flags
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Risk Level Classification</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">LOW</span>
                    <p className="text-muted-foreground">
                      Standard deals under $5,000 with verified brands, no conflicts, complete documentation
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">MEDIUM</span>
                    <p className="text-muted-foreground">
                      Deals $5,000-$10,000, new brands, or minor documentation issues
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm">HIGH</span>
                    <p className="text-muted-foreground">
                      Deals over $10,000, potential conflicts, prohibited categories, or compliance concerns
                    </p>
                  </div>
                </div>

                <h4 className="font-semibold mt-6">Common Compliance Flags</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Sponsor Conflict:</strong> Brand competes with institutional sponsor</li>
                  <li><strong>Prohibited Category:</strong> Industry not allowed (gambling, alcohol, etc.)</li>
                  <li><strong>Missing Documents:</strong> Required contract or disclosure not uploaded</li>
                  <li><strong>High Value:</strong> Deal exceeds mandatory review threshold</li>
                  <li><strong>Athlete Ineligible:</strong> Athlete does not meet eligibility requirements</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="title-ix">
            <AccordionTrigger className="text-lg font-semibold">
              Running Title IX Equity Reports
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Step 1: Access Title IX Compliance</h4>
                <p className="text-muted-foreground">
                  Navigate to Dashboard → Compliance → Title IX to view current compliance data.
                </p>

                <h4 className="font-semibold">Step 2: Review Current Year Data</h4>
                <p className="text-muted-foreground">
                  The dashboard shows enrollment ratios, athlete participation ratios, and revenue sharing
                  distribution by gender.
                </p>

                <h4 className="font-semibold">Step 3: Analyze Variance</h4>
                <p className="text-muted-foreground">
                  System calculates variance between enrollment ratios and compensation distribution.
                  Variance exceeding 5% requires action plan.
                </p>

                <h4 className="font-semibold">Step 4: Generate Report</h4>
                <p className="text-muted-foreground">
                  Click "Export Report" to generate comprehensive PDF for NCAA submission or internal review.
                </p>

                <h4 className="font-semibold">Best Practices</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Review Title IX compliance monthly</li>
                  <li>Document any remediation actions taken</li>
                  <li>Coordinate with Athletic Director on corrective measures</li>
                  <li>Maintain detailed records for audits</li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="documents">
            <AccordionTrigger className="text-lg font-semibold">
              Managing Document Submissions
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold">Required Documents</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>Signed NIL contract or agreement</li>
                  <li>Scope of work / deliverables description</li>
                  <li>Payment schedule</li>
                  <li>Brand contact information</li>
                  <li>Additional disclosures (if applicable)</li>
                </ul>

                <h4 className="font-semibold mt-4">Document Review Checklist</h4>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  <li>✓ All pages present and legible</li>
                  <li>✓ Signatures and dates complete</li>
                  <li>✓ Compensation clearly stated</li>
                  <li>✓ Term dates specified</li>
                  <li>✓ No conflicting clauses</li>
                </ul>

                <h4 className="font-semibold mt-4">Requesting Additional Documentation</h4>
                <p className="text-muted-foreground">
                  If documents are incomplete, use the "Request More Info" option in deal review.
                  Specify exactly what is needed and set a deadline for submission.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="best-practices">
            <AccordionTrigger className="text-lg font-semibold">
              Best Practices and Tips
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Review deals within 48 hours of submission to maintain athlete satisfaction</li>
                <li>Document all decisions with clear rationale in audit trail</li>
                <li>Communicate proactively with athletes and coaches about compliance requirements</li>
                <li>Set up notification preferences to receive alerts for high-priority deals</li>
                <li>Regularly update prohibited categories and institutional sponsor lists</li>
                <li>Coordinate with legal counsel on complex or high-value deals</li>
                <li>Maintain confidentiality of athlete financial information</li>
                <li>Stay current on NCAA rule changes and state legislation</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card>
          <CardHeader>
            <CardTitle>Download Complete SOP</CardTitle>
            <CardDescription>
              Get the full Compliance Officer Standard Operating Procedures as a PDF
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button>
              <Download className="mr-2 h-4 w-4" />
              Download PDF (28 pages)
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ComplianceOfficerSOP;